import { HttpMethod, HttpModel, MetaModel } from '@/Shared/domain'
import { axiosFetch, createQueryString } from '@/Shared/infrastructure'
import {
  getContactsData,
  getRestaurantData,
  getRestaurantFoodType,
  kindergartenData,
  orderStickers,
} from '@/Shared/infrastructure/endpoints'
import {
  ContactModel,
  ContactTypeId,
  FoodTypeModel,
  RestaurantModel,
  StoresRepository,
} from '@/Store/domain'
import { StoreForm, StoreStickersForm } from '@/Store/ui/hooks'

export function createApiStoreRepository(): StoresRepository {
  return new ApiStoreRepository()
}

type ApiReponseRestaurantModel = {
  merchantTargetId: number
  merchantCIF: string | null
  merchantName: string
  addressRoadTypeName: string | null
  addressStreetName: string
  addressStreetNumber: string
  addressAdditionalData1: string | null
  addressAdditionalData12: string | null
  addressZipCode: string
  addressTown: string
  addressProvince: string
  addressTelephone: string | null
  addressEmail: string
  merchantWebUrl: string | null
  merchantCoockTypeId: number | null
  merchantCoockType: string | null
  merchantIsOnlineStore: boolean
  merchantHasHomeDelivery: boolean
  merchantHasLocalPickup: boolean
  merchantLatitudeCoordinate: string | null
  merchantLongitudeCoordinate: string | null
}

type ApiRestaurantModel = {
  merchantId: number
  merchantParentId: number | null
  merchantParentName: string | null
  merchantBusinessName: string
  hasMerchantHardDependency: false
  masterGroupId: number | null
  masterGroupAlias: string | null
  superMasterGroupAlias: null
  merchantName: string
  merchantCIF: string
  addressRoadTypeName: string | null
  addressStreetName: string
  addressStreetNumber: string
  addressAdditionalData1: string | null
  addressAdditionalData12: string | null
  addressZipCode: string
  addressTown: string
  addressProvince: string
  addressTelephone: string | null
  addressEmail: string | null
  merchantWebUrl: string | null
  merchantCoockTypeId: number | null
  merchantCoockType: string | null
  merchantIsOnlineStore: boolean
  merchantHasHomeDelivery: boolean
  merchantHasLocalPickup: boolean
  merchantLatitudeCoordinate: string | null
  merchantLongitudeCoordinate: string | null
  adressId: number
}

type ApiRestaurant = {
  data: ApiRestaurantModel
  meta: MetaModel
}

const convertRestaurantDataToDomain = ({ data }: ApiRestaurant): RestaurantModel => ({
  name: data.merchantName,
  afiliateCode: data.merchantId,
  email: data.addressEmail || '',
  phone: data.addressTelephone ?? '',
  url: data.merchantWebUrl || '',
  food: {
    id: data.merchantCoockTypeId ?? null,
    name: data.merchantCoockType ?? null,
  },
  service: {
    physical: !data.merchantIsOnlineStore,
    delivery: data.merchantHasHomeDelivery,
    takeAway: data.merchantHasLocalPickup,
  },

  street: data.addressStreetName,
  number: data.addressStreetNumber,
  additionalInfo: data.addressAdditionalData12,
  floorBlockStair: data.addressAdditionalData1,
  postalCode: data.addressZipCode,
  city: data.addressTown,
  province: data.addressProvince,
  latitude: data.merchantLatitudeCoordinate ?? null,
  longitude: data.merchantLongitudeCoordinate ?? null,
  addressId: data.adressId,
})

const convertFormToApi = (data: StoreForm): ApiReponseRestaurantModel => ({
  merchantTargetId: data.afiliateCode,
  merchantName: data.merchantName,
  merchantCIF: '',
  addressRoadTypeName: '',
  addressStreetName: data.street,
  addressStreetNumber: data.number,
  addressAdditionalData1: data.floorBlockStair ?? null,
  addressAdditionalData12: data.additionalInformation ?? null,
  addressZipCode: data.postalCode,
  addressTown: data.city,
  addressProvince: data.province,
  addressTelephone: data.storePhone ?? null,
  addressEmail: data.addressEmail ?? '',
  merchantWebUrl: data.merchantWebUrl ?? null,
  merchantCoockTypeId: data.merchantCoockTypeId ?? null,
  merchantCoockType: data.merchantCoockType ?? null,
  merchantIsOnlineStore: !data.merchantIsOnlineStore,
  merchantHasHomeDelivery: data.merchantHasHomeDelivery ?? false,
  merchantHasLocalPickup: data.merchantHasLocalPickup ?? false,
  merchantLatitudeCoordinate: data.latitude?.toString() ?? null,
  merchantLongitudeCoordinate: data.longitude?.toString() ?? null,
})

type ApiFoodTypeModel = {
  code: number
  name: string
}

type ApiFoodType = {
  data: ApiFoodTypeModel[]
  meta: MetaModel
}

const convertFoodTypeDataToDomain = ({ data }: ApiFoodType): FoodTypeModel[] =>
  data.map(foodType => {
    return {
      id: foodType.code,
      name: foodType.name,
    }
  })

type ApiContactModel = {
  merchantId: number
  merchantParentId: number | null
  merchantParentName: string | null
  hasMerchantHardDependency: boolean
  merchantMasterId: number | null
  merchantMasterAlias: string | null
  superMasterGroupAlias: string | null
  merchantName: string
  contactId: number
  contacFirstName: string
  contactSecondName: string | null
  contactEmailAddress: string
  contactPhone: string
  contactTypeId: ContactTypeId
  contactIsEditable: boolean
  contactPhoneIsEditable: boolean
  productCode: number | null
}

type ApiContact = {
  data: ApiContactModel[]
  meta: MetaModel
}

const convertContactDataToDomain = ({ data }: ApiContact): ContactModel[] =>
  data.map(contact => {
    return {
      fullName: `${contact.contacFirstName} ${contact.contactSecondName ?? ''}`,
      email: contact.contactEmailAddress,
      phone: contact.contactPhone,
      position: contact.contactTypeId,
    }
  })

export class ApiStoreRepository implements StoresRepository {
  async GetRestaurantData(merchantTargetId: number): Promise<HttpModel<RestaurantModel>> {
    const response = await axiosFetch(
      getRestaurantData.concat(
        createQueryString({
          merchantTargetId: merchantTargetId.toString(),
        }),
      ),
      HttpMethod.GET,
      {},
    )

    if (!response.data) {
      return {
        data: {} as RestaurantModel,
        meta: response.meta,
      }
    }

    return {
      data: convertRestaurantDataToDomain(response),
      meta: response.meta,
    }
  }

    async GetKindergartenData(merchantTargetId: number): Promise<HttpModel<RestaurantModel>> {
    const response = await axiosFetch(
      kindergartenData.concat(
        createQueryString({
          merchantTargetId: merchantTargetId.toString(),
        }),
      ),
      HttpMethod.GET,
      {},
    )

    if (!response.data) {
      return {
        data: {} as RestaurantModel,
        meta: response.meta,
      }
    }

    return {
      data: convertRestaurantDataToDomain(response),
      meta: response.meta,
    }
  }

  async GetRestaurantFoodTypes(): Promise<HttpModel<FoodTypeModel[]>> {
    const response = await axiosFetch(getRestaurantFoodType, HttpMethod.GET, {})

    if (!response.data) {
      return {
        data: [] as FoodTypeModel[],
        meta: response.meta,
      }
    }

    return {
      data: convertFoodTypeDataToDomain(response),
      meta: response.meta,
    }
  }

  async SaveKindergartenData(data: StoreForm): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(
      kindergartenData,
      HttpMethod.POST,
      convertFormToApi(data),
    )

    return {
      data: response.data,
      meta: response.meta,
    }
  }

    async SaveRestaurantData(data: StoreForm): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(
      getRestaurantData,
      HttpMethod.POST,
      convertFormToApi(data),
    )

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async GetStoreContactsData(
    merchantTargetId: number,
  ): Promise<HttpModel<ContactModel[]>> {
    const response = await axiosFetch(
      getContactsData.concat(
        createQueryString({
          merchantTargetId: merchantTargetId.toString(),
          filter: '1',
        }),
      ),
      HttpMethod.GET,
      {},
    )

    if (!response.data) {
      return {
        data: [] as ContactModel[],
        meta: response.meta,
      }
    }

    return {
      data: convertContactDataToDomain(response),
      meta: response.meta,
    }
  }

  async OrderStoreStickers(data: StoreStickersForm): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(orderStickers, HttpMethod.POST, {
      merchantTargetId: data.merchantTargetId,
      adressId: data.addressId,
      materialQuantity: data.materialQuantity,
    })

    return {
      data: response.data,
      meta: response.meta,
    }
  }
}
